// src/components/Admin/tabs/FilesTab.jsx
import React, { useState, useEffect } from "react";
import {
  FileText,
  Table,
  BookOpen,
  HelpCircle,
  Database,
  MapPin,
  Briefcase,
  Check,
  X,
  Trash2,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import {
  uploadFile,
  deleteFile,
  listFilePaths,
} from "../../../services/adminService";
import FileUploader from "../shared/FileUploader";
import { useToast } from "../shared/ToastContext";

// ============================================
// HELPERS
// ============================================

const iconMap = {
  "file-text": FileText,
  table: Table,
  "book-open": BookOpen,
  "help-circle": HelpCircle,
  database: Database,
  "map-pin": MapPin,
  briefcase: Briefcase,
};

const getIcon = (iconName) => iconMap[iconName] || FileText;

// ============================================
// SINGLE FILE ROW
// ============================================

const FileRow = ({
  label,
  storagePath,
  fileName,
  fullStoragePath,
  icon = "file-text",
  accept,
  acceptLabel,
  status,
  onUpload,
  onDelete,
  onCheckStatus,
}) => {
  const [deleting, setDeleting] = useState(false);
  const Icon = getIcon(icon);

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${fileName}?`)) return;
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-3 px-4 bg-gray-50 rounded-lg">
      {/* Top row: icon + info (+ status/actions on sm+) */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Icon */}
        <div className="flex-shrink-0">
          <Icon size={16} className="text-gray-400" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 truncate">{label}</p>
          <p className="text-xs text-gray-400 font-mono truncate">
            {fullStoragePath || storagePath}
          </p>
        </div>
      </div>

      {/* Bottom row on mobile, inline on sm+: status + actions */}
      <div className="flex items-center gap-2 sm:gap-1 pl-7 sm:pl-0 flex-shrink-0">
        {/* Status */}
        <div className="flex-shrink-0">
          {status === "checking" ? (
            <RefreshCw size={14} className="text-gray-400 animate-spin" />
          ) : status === "uploaded" ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full">
              <Check size={12} />
              Uploaded
            </span>
          ) : status === "missing" ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-full">
              <X size={12} />
              Missing
            </span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <FileUploader
            onUpload={onUpload}
            accept={accept}
            acceptLabel={acceptLabel}
            compact
            currentFile={status === "uploaded" ? fileName : null}
          />
          {status === "uploaded" && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition disabled:opacity-50"
              title="Delete file"
            >
              {deleting ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// FILE SECTION (collapsible group)
// ============================================

const FileSection = ({
  title,
  subtitle,
  icon: SectionIcon,
  children,
  count,
}) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
      <div className="flex items-center gap-2.5">
        {SectionIcon && <SectionIcon size={18} className="text-gray-500" />}
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          )}
        </div>
        {count !== undefined && (
          <span className="text-xs text-gray-400">{count} files</span>
        )}
      </div>
    </div>
    <div className="p-4 space-y-2">{children}</div>
  </div>
);

// ============================================
// MAIN FILES TAB
// ============================================

const FilesTab = ({ clientSlug, config }) => {
  const toast = useToast();
  const [fileStatuses, setFileStatuses] = useState({});
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  const maps = config?.maps || [];
  const avatars = config?.avatars || [];
  const globalReports = config?.globalReports || [];

  // Build the full list of expected files
  const buildExpectedFiles = () => {
    const files = [];

    // Scored data files (one per map)
    maps.forEach((map) => {
      if (map.data?.scoredDataPath) {
        files.push({
          id: `scored_${map.id}`,
          category: "scored",
          label: `${map.label} — ${map.geographyType} Scored Data`,
          fileName: map.data.scoredDataPath,
          storagePath: map.data.scoredDataPath,
          fullStoragePath: `clients/${clientSlug}/scored/${map.data.scoredDataPath}`,
          icon: "database",
          accept: ".json",
          acceptLabel: "JSON files",
          mapId: map.id,
        });
      }
    });

    // Competitor data files (deduplicated — multiple maps may share the same file)
    const seenCompetitorPaths = new Set();
    maps.forEach((map) => {
      if (
        map.data?.competitorsPath &&
        !seenCompetitorPaths.has(map.data.competitorsPath)
      ) {
        seenCompetitorPaths.add(map.data.competitorsPath);
        const avatar = avatars.find((a) => a.id === map.avatarId);
        files.push({
          id: `competitors_${map.data.competitorsPath}`,
          category: "competitors",
          label: `${avatar?.name || map.avatarId} — Competitor Data`,
          fileName: map.data.competitorsPath.split("/").pop(),
          storagePath: map.data.competitorsPath,
          fullStoragePath: `clients/${clientSlug}/${map.data.competitorsPath}`,
          icon: "map-pin",
          accept: ".json",
          acceptLabel: "JSON files",
        });
      }
    });

    // Opportunity data files (deduplicated — all maps share the same client-wide file)
    const seenOpportunityPaths = new Set();
    maps.forEach((map) => {
      if (
        map.data?.opportunitiesPath &&
        !seenOpportunityPaths.has(map.data.opportunitiesPath)
      ) {
        seenOpportunityPaths.add(map.data.opportunitiesPath);
        files.push({
          id: `opportunities_${map.data.opportunitiesPath}`,
          category: "opportunities",
          label: "Opportunity Data",
          fileName: map.data.opportunitiesPath.split("/").pop(),
          storagePath: map.data.opportunitiesPath,
          fullStoragePath: `clients/${clientSlug}/${map.data.opportunitiesPath}`,
          icon: "briefcase",
          accept: ".json",
          acceptLabel: "JSON files",
        });
      }
    });

    // Map reports (ranked PDF, ranked XLSX, executive summary per map)
    maps.forEach((map) => {
      const reports = map.reports || {};
      Object.entries(reports).forEach(([key, report]) => {
        if (report?.storagePath) {
          const ext = report.fileName?.split(".").pop()?.toLowerCase();
          files.push({
            id: `report_${map.id}_${key}`,
            category: "mapReports",
            label: report.title || `${key} — ${map.label}`,
            fileName: report.fileName,
            storagePath: report.storagePath,
            fullStoragePath: `clients/${clientSlug}/${report.storagePath}`,
            icon: ext === "xlsx" ? "table" : "file-text",
            accept: ext === "xlsx" ? ".xlsx" : ".pdf",
            acceptLabel: ext === "xlsx" ? "Excel files" : "PDF files",
            mapId: map.id,
          });
        }
      });
    });

    // Avatar reports (methodology per avatar)
    avatars.forEach((avatar) => {
      const methodology = avatar.reports?.methodology;
      if (methodology?.storagePath) {
        files.push({
          id: `methodology_${avatar.id}`,
          category: "methodology",
          label: methodology.title || `${avatar.name} Methodology`,
          fileName: methodology.fileName,
          storagePath: methodology.storagePath,
          fullStoragePath: `clients/${clientSlug}/${methodology.storagePath}`,
          icon: "book-open",
          accept: ".pdf",
          acceptLabel: "PDF files",
          avatarId: avatar.id,
        });
      }
    });

    // Global reports (FAQ, etc.)
    globalReports.forEach((report) => {
      if (report?.storagePath) {
        const ext = report.fileName?.split(".").pop()?.toLowerCase();
        files.push({
          id: `global_${report.id}`,
          category: "global",
          label: report.title || report.fileName,
          fileName: report.fileName,
          storagePath: report.storagePath,
          fullStoragePath: `clients/${clientSlug}/${report.storagePath}`,
          icon: report.icon || "file-text",
          accept: ext === "md" ? ".md" : `.${ext}`,
          acceptLabel:
            ext === "md" ? "Markdown files" : `${ext.toUpperCase()} files`,
        });
      }
    });

    return files;
  };

  const expectedFiles = buildExpectedFiles();

  // Check which files exist in Storage
  // Uses listFiles to batch-fetch directory contents (2 calls instead of N),
  // avoiding cross-service rule throttling that caused all files to show "missing"
  const checkAllStatuses = async () => {
    if (!clientSlug) return;
    setChecking(true);
    setError(null);

    // Show "checking" state for all files
    const statuses = {};
    for (const file of expectedFiles) {
      statuses[file.id] = "checking";
    }
    setFileStatuses({ ...statuses });

    try {
      // Fetch actual files from Storage in 4 lightweight calls (no metadata)
      const [
        scoredResult,
        reportsResult,
        competitorsResult,
        opportunitiesResult,
      ] = await Promise.all([
        listFilePaths(`clients/${clientSlug}/scored`).catch(() => []),
        listFilePaths(`clients/${clientSlug}/reports`).catch(() => []),
        listFilePaths(`clients/${clientSlug}/competitors`).catch(() => []),
        listFilePaths(`clients/${clientSlug}/opportunities`).catch(() => []),
      ]);

      // Build a Set of actual full paths for fast lookup
      const actualPaths = new Set([
        ...scoredResult.map((f) => f.fullPath),
        ...reportsResult.map((f) => f.fullPath),
        ...competitorsResult.map((f) => f.fullPath),
        ...opportunitiesResult.map((f) => f.fullPath),
      ]);

      // Compare each expected file against actual paths
      for (const file of expectedFiles) {
        statuses[file.id] = actualPaths.has(file.fullStoragePath)
          ? "uploaded"
          : "missing";
      }

      setFileStatuses({ ...statuses });
    } catch (err) {
      console.error("Failed to check file statuses:", err);
      setError("Failed to check file statuses");
    } finally {
      setChecking(false);
    }
  };

  // Check statuses on mount
  useEffect(() => {
    checkAllStatuses();
  }, [clientSlug]);

  // Upload handler
  const handleUpload = async (file, expectedFile) => {
    try {
      await uploadFile(expectedFile.fullStoragePath, file);
      setFileStatuses((prev) => ({
        ...prev,
        [expectedFile.id]: "uploaded",
      }));
      toast.success(`Uploaded ${expectedFile.fileName}`);
    } catch (err) {
      toast.error(`Failed to upload ${expectedFile.fileName}`);
    }
  };

  // Delete handler
  const handleDelete = async (expectedFile) => {
    try {
      await deleteFile(expectedFile.fullStoragePath);
      setFileStatuses((prev) => ({
        ...prev,
        [expectedFile.id]: "missing",
      }));
      toast.success(`Deleted ${expectedFile.fileName}`);
    } catch (err) {
      setError(`Failed to delete ${expectedFile.fileName}`);
      toast.error(`Failed to delete ${expectedFile.fileName}`);
    }
  };

  // Group files by category
  const scoredFiles = expectedFiles.filter((f) => f.category === "scored");
  const competitorFiles = expectedFiles.filter(
    (f) => f.category === "competitors",
  );
  const opportunityFiles = expectedFiles.filter(
    (f) => f.category === "opportunities",
  );
  const mapReportFiles = expectedFiles.filter(
    (f) => f.category === "mapReports",
  );
  const methodologyFiles = expectedFiles.filter(
    (f) => f.category === "methodology",
  );
  const globalFiles = expectedFiles.filter((f) => f.category === "global");

  // Stats
  const uploadedCount = Object.values(fileStatuses).filter(
    (s) => s === "uploaded",
  ).length;
  const missingCount = Object.values(fileStatuses).filter(
    (s) => s === "missing",
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">
            File Management
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Upload and manage files for{" "}
            <span className="font-mono">{clientSlug}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={checkAllStatuses}
          disabled={checking}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg transition disabled:opacity-50"
        >
          <RefreshCw size={14} className={checking ? "animate-spin" : ""} />
          Refresh Status
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">
            {expectedFiles.length}
          </div>
          <div className="text-xs text-gray-500">Expected</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-2xl font-bold text-emerald-600">
            {uploadedCount}
          </div>
          <div className="text-xs text-gray-500">Uploaded</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div
            className={`text-2xl font-bold ${missingCount > 0 ? "text-amber-600" : "text-gray-300"}`}
          >
            {missingCount}
          </div>
          <div className="text-xs text-gray-500">Missing</div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* No config warning */}
      {expectedFiles.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <AlertCircle size={24} className="mx-auto text-amber-400 mb-2" />
          <p className="text-sm text-amber-700">
            No files expected. Configure avatars and maps in the Avatars & Maps
            tab first — file paths are derived from the config.
          </p>
        </div>
      )}

      {/* Scored Data */}
      {scoredFiles.length > 0 && (
        <FileSection
          title="Scored Data"
          subtitle="JSON files with scored demographics for each map"
          icon={Database}
          count={scoredFiles.length}
        >
          {scoredFiles.map((file) => (
            <FileRow
              key={file.id}
              label={file.label}
              storagePath={file.storagePath}
              fileName={file.fileName}
              fullStoragePath={file.fullStoragePath}
              icon={file.icon}
              accept={file.accept}
              acceptLabel={file.acceptLabel}
              status={fileStatuses[file.id]}
              onUpload={(f) => handleUpload(f, file)}
              onDelete={() => handleDelete(file)}
            />
          ))}
        </FileSection>
      )}

      {/* Competitor Data */}
      {competitorFiles.length > 0 && (
        <FileSection
          title="Competitor Data"
          subtitle="JSON files with competitor locations per avatar"
          icon={MapPin}
          count={competitorFiles.length}
        >
          {competitorFiles.map((file) => (
            <FileRow
              key={file.id}
              label={file.label}
              storagePath={file.storagePath}
              fileName={file.fileName}
              fullStoragePath={file.fullStoragePath}
              icon={file.icon}
              accept={file.accept}
              acceptLabel={file.acceptLabel}
              status={fileStatuses[file.id]}
              onUpload={(f) => handleUpload(f, file)}
              onDelete={() => handleDelete(file)}
            />
          ))}
        </FileSection>
      )}

      {/* Opportunity Data */}
      {opportunityFiles.length > 0 && (
        <FileSection
          title="Opportunity Data"
          subtitle="JSON files with opportunity locations (client-wide)"
          icon={Briefcase}
          count={opportunityFiles.length}
        >
          {opportunityFiles.map((file) => (
            <FileRow
              key={file.id}
              label={file.label}
              storagePath={file.storagePath}
              fileName={file.fileName}
              fullStoragePath={file.fullStoragePath}
              icon={file.icon}
              accept={file.accept}
              acceptLabel={file.acceptLabel}
              status={fileStatuses[file.id]}
              onUpload={(f) => handleUpload(f, file)}
              onDelete={() => handleDelete(file)}
            />
          ))}
        </FileSection>
      )}

      {/* Map Reports */}
      {mapReportFiles.length > 0 && (
        <FileSection
          title="Map Reports"
          subtitle="Ranked lists (PDF & Excel) and executive summaries per map"
          icon={FileText}
          count={mapReportFiles.length}
        >
          {mapReportFiles.map((file) => (
            <FileRow
              key={file.id}
              label={file.label}
              storagePath={file.storagePath}
              fileName={file.fileName}
              fullStoragePath={file.fullStoragePath}
              icon={file.icon}
              accept={file.accept}
              acceptLabel={file.acceptLabel}
              status={fileStatuses[file.id]}
              onUpload={(f) => handleUpload(f, file)}
              onDelete={() => handleDelete(file)}
            />
          ))}
        </FileSection>
      )}

      {/* Methodology Reports */}
      {methodologyFiles.length > 0 && (
        <FileSection
          title="Methodology Reports"
          subtitle="One methodology PDF per avatar (business model)"
          icon={BookOpen}
          count={methodologyFiles.length}
        >
          {methodologyFiles.map((file) => (
            <FileRow
              key={file.id}
              label={file.label}
              storagePath={file.storagePath}
              fileName={file.fileName}
              fullStoragePath={file.fullStoragePath}
              icon={file.icon}
              accept={file.accept}
              acceptLabel={file.acceptLabel}
              status={fileStatuses[file.id]}
              onUpload={(f) => handleUpload(f, file)}
              onDelete={() => handleDelete(file)}
            />
          ))}
        </FileSection>
      )}

      {/* Global Reports */}
      {globalFiles.length > 0 && (
        <FileSection
          title="Global Reports"
          subtitle="Client-wide reports shared across all maps (FAQ, etc.)"
          icon={HelpCircle}
          count={globalFiles.length}
        >
          {globalFiles.map((file) => (
            <FileRow
              key={file.id}
              label={file.label}
              storagePath={file.storagePath}
              fileName={file.fileName}
              fullStoragePath={file.fullStoragePath}
              icon={file.icon}
              accept={file.accept}
              acceptLabel={file.acceptLabel}
              status={fileStatuses[file.id]}
              onUpload={(f) => handleUpload(f, file)}
              onDelete={() => handleDelete(file)}
            />
          ))}
        </FileSection>
      )}
    </div>
  );
};

export default FilesTab;
